<!-- src/views/Signup.vue -->
<template>
  <div class="container coredeck-page" style="max-width: 720px; margin-top: 32px">
    <div class="card">
      <div class="card-content">
        <h4 class="center-align" style="margin-top: 0">Sign up</h4>

        <div class="center-align grey-text text-darken-1" style="margin-top: -6px">
          <span v-if="hasInvite && inviteAuthMode === 'google'">
            Use Google to complete this invite.
          </span>
          <span v-else-if="hasInvite && inviteAuthMode === 'email'">
            Use the invited email address to complete this invite.
          </span>
          <span v-else-if="hasInvite"> Loading invite signup method... </span>
          <span v-else> Signup is only available from a valid invite link. </span>
        </div>

        <!-- Invite banner -->
        <div v-if="hasInvite" class="card-panel teal lighten-5" style="margin-top: 16px">
          <strong>Invitation detected</strong>
          <p class="grey-text text-darken-2" style="margin: 6px 0 0">
            This link will assign your venue and permissions automatically.
          </p>

          <div v-if="inviteLoading" class="grey-text" style="margin-top: 8px">
            Loading invite details...
          </div>

          <div v-else-if="inviteError" class="red-text" style="margin-top: 8px">
            <div><strong>Invite load failed</strong></div>
            <pre style="white-space: pre-wrap; margin: 8px 0 0">{{ inviteError }}</pre>
          </div>

          <div v-else class="grey-text text-darken-2" style="margin-top: 8px">
            <div><strong>Invite for:</strong> {{ invitedEmail || "Unknown" }}</div>
            <div><strong>Signup method:</strong> {{ inviteAuthModeLabel }}</div>
            <div><strong>Venue:</strong> {{ invitedVenue || "— (Operations User)" }}</div>
            <div><strong>Role:</strong> {{ invitedRole || "Unknown" }}</div>
            <div>
              <strong>Operations:</strong> {{ invitedIsOperations ? "Yes" : "No" }}
            </div>
          </div>
        </div>

        <!-- Signed in summary -->
        <div v-if="user" style="margin-top: 16px" class="grey-text text-darken-2">
          Signed in as <strong>{{ user.displayName || user.email }}</strong>
          <div class="grey-text" style="font-size: 0.9rem">{{ user.email }}</div>
        </div>

        <!-- AUTH OPTIONS - INVITE ONLY -->
        <div
          v-if="hasInvite && !inviteLoading && !inviteError"
          class="row"
          style="margin-top: 18px; margin-bottom: 0"
        >
          <!-- Google sign-in -->
          <div
            v-if="showGoogleSignup"
            :class="googleColumnClass"
            class="center-align"
            style="margin-bottom: 10px"
          >
            <button
              class="google-signup-btn"
              type="button"
              :disabled="busy"
              @click="handleGoogleSignup"
            >
              <span class="google-signup-icon" aria-hidden="true">
                <svg viewBox="0 0 48 48" width="26" height="26">
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                  />
                  <path fill="none" d="M0 0h48v48H0z" />
                </svg>
              </span>

              <span>Continue with Google</span>
            </button>

            <button
              v-if="user"
              class="btn-flat waves-effect"
              :disabled="busy"
              style="margin-top: 8px"
              @click="switchAccount"
            >
              Sign out / switch account
            </button>
          </div>

          <!-- Email signup -->
          <div v-if="showEmailSignup" :class="emailColumnClass">
            <div class="card-panel" style="padding: 14px; margin: 0">
              <div style="font-weight: 600">Sign up with Email</div>
              <div
                class="grey-text text-darken-1"
                style="font-size: 13px; margin-top: 4px"
              >
                <span v-if="hasInvite && invitedEmail">
                  Use {{ invitedEmail }} for this invite.
                </span>
                <span v-else> Use the invited email address. </span>
              </div>

              <div class="row" style="margin-top: 8px; margin-bottom: 0">
                <div class="input-field col s12" style="margin-top: 6px">
                  <input
                    id="email_up"
                    type="email"
                    v-model.trim="emailUp"
                    autocomplete="email"
                    :disabled="busy || lockedEmailSignup"
                  />
                  <label for="email_up" :class="{ active: !!emailUp }">Email</label>
                </div>

                <div class="input-field col s12" style="margin-top: 6px">
                  <input
                    id="password_up"
                    type="password"
                    v-model="passwordUp"
                    autocomplete="new-password"
                    :disabled="busy"
                  />
                  <label for="password_up">Password</label>
                  <span class="helper-text">Minimum 6 characters (Firebase default)</span>
                </div>

                <div class="col s12" style="margin-top: 8px">
                  <button
                    class="btn blue-grey darken-4 waves-effect waves-light"
                    :disabled="busy || !emailUp || !passwordUp"
                    @click="handleEmailSignup"
                    style="width: 100%"
                  >
                    Create Email Account
                  </button>
                </div>
              </div>

              <div
                v-if="emailInfo"
                class="green-text"
                style="margin-top: 10px; white-space: pre-wrap"
              >
                {{ emailInfo }}
              </div>
            </div>
          </div>
        </div>

        <!-- Invite completion -->
        <div v-if="hasInvite" style="margin-top: 18px">
          <div class="grey-text text-darken-2" style="margin-bottom: 10px">
            Completing your invited signup...
          </div>

          <button
            class="btn waves-effect waves-light"
            :disabled="busy || !user || !canFinalizeInvite"
            @click="completeInviteSignup"
          >
            <i class="material-icons left">verified_user</i>
            Complete signup
          </button>

          <div v-if="disableReason" class="red-text" style="margin-top: 12px">
            {{ disableReason }}
          </div>

          <!-- Determinate preloader -->
          <div v-if="signupLoading" class="card-panel" style="margin-top: 14px">
            <div style="font-weight: 600">{{ signupStage }}</div>
            <div class="grey-text text-darken-1" style="margin-top: 4px">
              {{ signupDetail }}
            </div>

            <div class="progress" style="margin-top: 12px">
              <div class="determinate" :style="{ width: signupProgress + '%' }"></div>
            </div>

            <div class="grey-text" style="margin-top: 6px">{{ signupProgress }}%</div>
          </div>

          <div class="card-panel" style="margin-top: 12px">
            <div class="grey-text text-darken-2">
              If you’re on a shared computer or the wrong profile keeps auto-signing you
              in:
              <ul style="margin-top: 8px">
                <li>Open an Incognito window (Ctrl+Shift+N / Cmd+Shift+N)</li>
                <li>Paste the invite link there</li>
              </ul>
            </div>

            <div style="margin-top: 10px">
              <button
                class="btn-small waves-effect"
                @click="copyInviteLink"
                :disabled="busy"
              >
                Copy invite link
              </button>
              <span v-if="copied" class="green-text" style="margin-left: 10px">
                Copied
              </span>
            </div>
          </div>
        </div>

        <!-- No invite flow -->
        <div v-else style="margin-top: 18px">
          <div class="card-panel amber lighten-5 orange-text text-darken-4">
            <strong>Invite required</strong>
            <p style="margin: 8px 0 0">
              Signup is only available from a valid invite link. Please use the invite
              link sent to your email address.
            </p>
          </div>
        </div>

        <!-- Errors -->
        <div v-if="error" class="red-text" style="margin-top: 14px">
          <div><strong>Error</strong></div>
          <pre style="white-space: pre-wrap; margin: 8px 0 0">{{ error }}</pre>
        </div>
      </div>

      <div class="card-action center-align">
        <router-link class="orange-text text-darken-2" :to="{ name: 'Login' }">
          ALREADY HAVE AN ACCOUNT? BACK TO LOGIN
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import app from "@/firebase";
import { ref, computed, nextTick, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";

import { auth, db } from "@/firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

import { callEdge, refreshFirebaseClaims } from "@/scripts/sqlApiHelper/edgeApi";

/* global M */

const router = useRouter();
const route = useRoute();

/**
 * Cloud Functions (must match deployed region)
 */
const FUNCTIONS_REGION = "us-central1";
const functions = getFunctions(app, FUNCTIONS_REGION);
const getInviteDetails = httpsCallable(functions, "getInviteDetails");
const finalizeInvite = httpsCallable(functions, "finalizeInvite");

const user = ref(null);
const busy = ref(false);
const error = ref("");
const copied = ref(false);

const inviteLoading = ref(false);
const inviteError = ref("");
const invitedEmail = ref("");
const invitedVenue = ref("");
const invitedRole = ref("");
const invitedIsOperations = ref(false);

// Email signup state
const emailUp = ref("");
const passwordUp = ref("");
const emailInfo = ref("");

// Determinate preloader state
const signupLoading = ref(false);
const signupProgress = ref(0);
const signupStage = ref("");
const signupDetail = ref("");

function setSignupProgress(pct, stage, detail) {
  signupProgress.value = Math.max(0, Math.min(100, Math.round(pct)));
  signupStage.value = stage || "";
  signupDetail.value = detail || "";
}

/**
 * Invite params
 */
const inviteId = computed(() =>
  String(route.query.invite || route.query.inviteId || "").trim()
);

const inviteToken = computed(() =>
  String(route.query.token || route.query.inviteToken || "").trim()
);

const hasInvite = computed(() => !!inviteId.value && !!inviteToken.value);

const signedInEmail = computed(() => String(user.value?.email || "").toLowerCase());

const emailsMatch = computed(() => {
  if (!invitedEmail.value || !signedInEmail.value) return false;
  return invitedEmail.value.toLowerCase() === signedInEmail.value.toLowerCase();
});

const inviteEmailDomain = computed(() => {
  const email = String(invitedEmail.value || "")
    .trim()
    .toLowerCase();
  if (!email.includes("@")) return "";
  return email.split("@").pop() || "";
});

const inviteAuthMode = computed(() => {
  if (!hasInvite.value) return "none";
  if (!invitedEmail.value) return "unknown";

  const domain = inviteEmailDomain.value;

  if (domain === "gmail.com" || domain === "googlemail.com") {
    return "google";
  }

  return "email";
});

const inviteAuthModeLabel = computed(() => {
  if (!hasInvite.value) return "Invite required";
  if (inviteAuthMode.value === "google") return "Google";
  if (inviteAuthMode.value === "email") return "Email";
  return "Unknown";
});

const showGoogleSignup = computed(() => {
  if (!hasInvite.value) return false;
  return inviteAuthMode.value === "google" || inviteAuthMode.value === "unknown";
});

const showEmailSignup = computed(() => {
  if (!hasInvite.value) return false;
  return inviteAuthMode.value === "email" || inviteAuthMode.value === "unknown";
});

const googleColumnClass = computed(() => {
  return showEmailSignup.value ? "col s12 m6" : "col s12";
});

const emailColumnClass = computed(() => {
  return showGoogleSignup.value ? "col s12 m6" : "col s12";
});

const lockedEmailSignup = computed(() => {
  return hasInvite.value && inviteAuthMode.value === "email" && !!invitedEmail.value;
});

const canFinalizeInvite = computed(() => {
  if (!hasInvite.value) return false;
  if (!user.value) return false;
  if (inviteLoading.value) return false;
  if (inviteError.value) return false;
  if (!invitedEmail.value) return false;
  return emailsMatch.value;
});

const disableReason = computed(() => {
  if (!hasInvite.value) return "";

  if (!user.value) {
    if (inviteAuthMode.value === "google") {
      return "Please sign up with Google to accept this invite.";
    }

    if (inviteAuthMode.value === "email") {
      return "Please create the email account to accept this invite.";
    }

    return "Please sign in to accept this invite.";
  }

  if (inviteLoading.value) return "Loading invite details...";
  if (inviteError.value) return "Invite details could not be loaded.";
  if (!invitedEmail.value) return "Invite details not loaded yet.";

  if (!emailsMatch.value) {
    return `This invite is for ${invitedEmail.value}. Please sign in with that email.`;
  }

  return "";
});

watch(
  () => invitedEmail.value,
  async (email) => {
    if (hasInvite.value && inviteAuthMode.value === "email" && email) {
      emailUp.value = String(email).toLowerCase();

      await nextTick();
      if (window.M?.updateTextFields) window.M.updateTextFields();
    }
  }
);

function buildProvider() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
}

function formatCallableError(e) {
  const code = e?.code || "";
  const message = e?.message || String(e);
  const details = e?.details ? JSON.stringify(e.details, null, 2) : "";
  return [code, message, details].filter(Boolean).join("\n");
}

async function waitForUserDoc(uid, timeoutMs = 15000, pollMs = 700) {
  const started = Date.now();

  while (true) {
    const snap = await getDoc(doc(db, "users", uid));
    if (snap.exists()) return snap.data();

    const elapsed = Date.now() - started;
    if (elapsed >= timeoutMs) {
      throw new Error("Timed out waiting for Firestore user document.");
    }

    const soft = Math.min(65, 45 + Math.floor((elapsed / timeoutMs) * 20));

    setSignupProgress(
      soft,
      "Creating user record",
      "Confirming Firestore user document…"
    );

    await new Promise((r) => setTimeout(r, pollMs));
  }
}

async function loadInviteDetails() {
  if (!hasInvite.value) return;

  inviteLoading.value = true;
  inviteError.value = "";
  invitedEmail.value = "";
  invitedVenue.value = "";
  invitedRole.value = "";
  invitedIsOperations.value = false;

  try {
    const res = await getInviteDetails({
      inviteId: inviteId.value,
      token: inviteToken.value,
    });

    const d = res.data || {};

    invitedEmail.value = String(d.email || "").toLowerCase();
    invitedVenue.value = String(d.venueId || "");
    invitedRole.value = String(d.role || "");
    invitedIsOperations.value = !!(d.isOperations || d.is_operations || d.operations);

    if (invitedEmail.value && inviteAuthMode.value === "email") {
      emailUp.value = invitedEmail.value;
    }

    await nextTick();
    if (window.M?.updateTextFields) window.M.updateTextFields();
  } catch (e) {
    console.error("[getInviteDetails failed]", e);
    inviteError.value = formatCallableError(e);
  } finally {
    inviteLoading.value = false;
  }
}

async function handleGoogleSignup() {
  error.value = "";
  emailInfo.value = "";

  if (!hasInvite.value) {
    error.value = "Signup is only available from a valid invite link.";
    return;
  }

  if (inviteAuthMode.value === "email") {
    error.value = "This invite uses email signup, not Google signup.";
    return;
  }

  busy.value = true;

  try {
    const result = await signInWithPopup(auth, buildProvider());
    user.value = result.user;

    if (hasInvite.value) {
      await loadInviteDetails();
    }
  } catch (e) {
    console.error(e);
    error.value = "Google sign-in failed.\n" + formatCallableError(e);
  } finally {
    busy.value = false;
  }
}

async function handleEmailSignup() {
  error.value = "";
  emailInfo.value = "";

  if (!hasInvite.value) {
    error.value = "Signup is only available from a valid invite link.";
    return;
  }

  if (inviteAuthMode.value === "google") {
    error.value = "This invite uses Google signup, not email signup.";
    return;
  }

  busy.value = true;

  try {
    // If invite exists, strongly enforce invited email
    if (
      invitedEmail.value &&
      String(emailUp.value).toLowerCase() !== invitedEmail.value.toLowerCase()
    ) {
      throw new Error(
        `This invite is for ${invitedEmail.value}. Please use that email address.`
      );
    }

    const result = await createUserWithEmailAndPassword(
      auth,
      emailUp.value,
      passwordUp.value
    );

    user.value = result.user;
    emailInfo.value = "Account created.";

    // // Recommended: verification email (non-blocking to continue the invite flow)
    // try {
    //   await sendEmailVerification(result.user);
    //   emailInfo.value = "Account created.";
    // } catch (e) {
    //   console.warn("[sendEmailVerification failed]", e);
    //   emailInfo.value = "Account created. (Verification email could not be sent.)";
    // }

    // Materialize label refresh
    await nextTick();
    if (window.M?.updateTextFields) window.M.updateTextFields();

    if (hasInvite.value) {
      await loadInviteDetails();
    }
  } catch (e) {
    console.error(e);
    error.value = "Email signup failed.\n" + formatCallableError(e);
  } finally {
    busy.value = false;
  }
}

async function switchAccount() {
  error.value = "";
  emailInfo.value = "";
  busy.value = true;

  try {
    await signOut(auth);
    user.value = null;

    if (hasInvite.value) {
      await loadInviteDetails();
    } else {
      invitedEmail.value = "";
      invitedVenue.value = "";
      invitedRole.value = "";
      invitedIsOperations.value = false;
      inviteError.value = "";
      emailUp.value = "";
      passwordUp.value = "";
    }
  } catch (e) {
    console.error(e);
    error.value = "Could not sign out.\n" + formatCallableError(e);
  } finally {
    busy.value = false;
  }
}

async function completeInviteSignup() {
  if (!hasInvite.value) {
    error.value = "Signup is only available from a valid invite link.";
    return;
  }

  if (!user.value) {
    error.value = "You need to sign in first.";
    return;
  }

  // If invite details weren't loaded yet, fetch now.
  if (!invitedEmail.value && !inviteLoading.value && !inviteError.value) {
    await loadInviteDetails();
  }

  if (!canFinalizeInvite.value) {
    error.value = disableReason.value || "Invite cannot be completed yet.";
    return;
  }

  error.value = "";
  busy.value = true;

  // Show determinate preloader immediately
  signupLoading.value = true;
  setSignupProgress(10, "Starting", "Finalising invite…");

  try {
    // 1) Finalize invite (writes Firestore user doc, sets claims)
    setSignupProgress(20, "Finalising invite", "Writing your user record…");

    await finalizeInvite({
      inviteId: inviteId.value,
      token: inviteToken.value,
    });

    // 2) Confirm user doc exists and determine ops + venue requirements
    setSignupProgress(45, "Creating user record", "Confirming Firestore user document…");

    const d = await waitForUserDoc(user.value.uid);

    const isOperations =
      d?.isOperations === true ||
      d?.is_operations === true ||
      d?.operations === true ||
      invitedIsOperations.value === true;

    const venueId = String(
      d?.activeVenueId ||
        d?.venue_id ||
        d?.venueId ||
        d?.venue ||
        invitedVenue.value ||
        ""
    ).trim();

    // Only require venue for NON-ops users
    if (!isOperations && !venueId) {
      throw new Error("User record missing venue id (venue_id/venueId/venue).");
    }

    // Do not force a venue into storage for ops users (they choose later)
    if (!isOperations && venueId) {
      localStorage.setItem("venue", venueId);
    }

    // 3) Refresh claims
    setSignupProgress(70, "Refreshing access", "Refreshing your permissions…");
    await refreshFirebaseClaims();

    // 4) Provision Supabase membership NOW (blocking)
    // ops users have no fixed venue membership; skip this step
    if (!isOperations) {
      setSignupProgress(85, "Provisioning access", "Syncing venue access…");

      await callEdge(
        "sync-user-venue-from-firestore",
        { __noVenueContext: true },
        { forceRefresh: true }
      );
    } else {
      setSignupProgress(
        85,
        "Provisioning access",
        "Operations user created (venue selected later)…"
      );

      await new Promise((r) => setTimeout(r, 250));
    }

    setSignupProgress(100, "Complete", "Signup completed.");

    // Redirect to Login as logged-out user
    await signOut(auth);

    signupLoading.value = false;
    busy.value = false;

    router.push({ name: "Login" });
  } catch (e) {
    console.error("[completeInviteSignup failed]", e);

    signupLoading.value = false;
    busy.value = false;

    error.value =
      "Signup succeeded, but provisioning failed.\n" +
      "Please refresh and try again.\n\n" +
      formatCallableError(e);
  }
}

async function copyInviteLink() {
  copied.value = false;

  try {
    await navigator.clipboard.writeText(window.location.href);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1500);
  } catch (e) {
    console.error(e);
    error.value = "Could not copy link. Please copy from the address bar.";
  }
}

onMounted(async () => {
  if (hasInvite.value) {
    await loadInviteDetails();
  }
});

onAuthStateChanged(auth, async (currentUser) => {
  user.value = currentUser || null;

  if (hasInvite.value) {
    await loadInviteDetails();
    return;
  }

  // Signup is invite-only. If someone reaches /signup while already signed in
  // without an invite, sign them out so the page cannot be used as a backdoor
  // signup/venue assignment flow.
  if (user.value && !hasInvite.value) {
    await signOut(auth);
    user.value = null;
  }
});
</script>

<style scoped>
select.browser-default {
  width: 100%;
}

.google-signup-btn {
  width: 100%;
  min-height: 66px;
  border: 1px solid #d6e1f5;
  border-radius: 8px;
  background: #ffffff;
  color: #202124;
  font-size: 21px;
  font-weight: 600;
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 22px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(60, 64, 67, 0.18);
  transition: background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.google-signup-btn:hover:not(:disabled) {
  background: #f8fbff;
  border-color: #c7d7f3;
  box-shadow: 0 2px 5px rgba(60, 64, 67, 0.22);
}

.google-signup-btn:focus {
  outline: none;
  border-color: #8ab4f8;
  box-shadow: 0 0 0 2px rgba(138, 180, 248, 0.25);
}

.google-signup-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.google-signup-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

/* =========================================================
   Mobile optimisation only — logic unchanged
   ========================================================= */

@media only screen and (max-width: 600px) {
  .row.valign-wrapper {
    display: block;
  }

  .card .card-content {
    padding: 14px !important;
  }

  .btn,
  .btn-small,
  button.btn,
  button.btn-small {
    width: 100%;
    margin: 4px 0;
  }

  .right-align {
    text-align: left !important;
  }

  table th,
  table td {
    white-space: nowrap;
  }

  .responsive-table,
  .table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>